import { CURSOR_REJECTED_PROBLEM_TYPE } from '@misumi/shared'
import { apiClient } from '../../lib/api/client'
import { CursorRejectedError } from '../../lib/list/useCursorList'
import { isProblemType, problemTitle } from '../parts/usePartsLibrary'
import { serverStatusesForTab } from './domain'
import type { RfqListResponse, RfqServerTabId } from './domain'

/**
 * UID-603 (ADR-0176) — the paginated `GET /rfq` seam `RfqListPage` consumes,
 * mirroring `PartsLibraryService.listLibrary`'s shape. `tab` selects D5's
 * per-tab server-side `status` filter; `cursor`/`limit` are the D1.2 optional
 * pagination params.
 *
 * The query is expressed as a TAB, not as a status, because the contract's
 * `status` param is a repeatable SET and a tab maps onto one-to-three of them
 * (`serverStatusesForTab`, derived from `tabForStatus`). Keeping the
 * expansion inside this one function is what stops a caller from sending the
 * tab id as a bare single `?status=` and silently losing every `quoting` /
 * `accepted` / `declined` row.
 */
export interface RfqListQuery {
  readonly tab?: RfqServerTabId
}

export interface RfqListPageOptions {
  readonly cursor?: string
  readonly limit: number
}

export interface RfqListService {
  listRfqs(query: RfqListQuery, opts: RfqListPageOptions): Promise<RfqListResponse>
}

/**
 * REAL BFF-backed call. Fully generated-type driven — `status` (repeatable),
 * `limit` and `cursor` are all declared on `operations['listRfqs']` and the
 * response IS the `RfqListPage` envelope, so there is no cast at this
 * boundary in either direction.
 */
async function bffListRfqs(
  query: RfqListQuery,
  opts: RfqListPageOptions,
): Promise<RfqListResponse> {
  const { data, error, response } = await apiClient.GET('/rfq', {
    params: {
      query: {
        // Repeatable `?status=a&status=b` — the tab's whole status set, never
        // the tab id itself (see `RfqListQuery`).
        ...(query.tab !== undefined ? { status: [...serverStatusesForTab(query.tab)] } : {}),
        limit: opts.limit,
        ...(opts.cursor !== undefined ? { cursor: opts.cursor } : {}),
      },
    },
  })
  if (error || !data) {
    const message = problemTitle(error, 'We couldn’t load your RFQs. Try again.')
    // M2 (code-review UID-603 fourth pass) — a cursor-carrying request that
    // 400s is `rfq-list-cursor.ts`'s deliberate "cursor doesn't resolve"
    // rejection (ADR-0176 D2.1: never a silently empty page). Only possible
    // when THIS call sent a `cursor` at all — page 1 (no cursor) 400ing
    // would be a different, non-retriable-via-reload validation failure.
    //
    // Review W4 (fifth pass) — gated on the Problem `type` discriminator too
    // (see `usePartsLibrary.ts`'s `isProblemType` doc comment): the RFQ
    // route's OWN unrelated `workspaceId`+`limit` 400 could otherwise arrive
    // on a request that also happens to carry `cursor`, and must not be
    // mistaken for a dead cursor.
    if (
      opts.cursor !== undefined &&
      response.status === 400 &&
      isProblemType(error, CURSOR_REJECTED_PROBLEM_TYPE)
    ) {
      throw new CursorRejectedError(message)
    }
    throw new Error(message)
  }
  return data
}

export const defaultRfqListService: RfqListService = {
  listRfqs: bffListRfqs,
}
